// BookSessionPage.jsx
import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext } from '../lib/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const BookSessionPage = () => {
  const { linkId } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useContext(AuthContext);
  const [linkDetails, setLinkDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedStartTime, setSelectedStartTime] = useState("");
  const [selectedEndTime, setSelectedEndTime] = useState("");

  useEffect(() => {
    const fetchLinkDetails = async () => {
      if (!accessToken) {
        setError("You must be logged in to book a session.");
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`https://quirkleai.com/api/checkout-link/${linkId}/`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        setLinkDetails(response.data);
      } catch (err) {
        setError("This booking link is either invalid or has expired.");
        console.error("Error fetching link details:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchLinkDetails();
  }, [linkId, accessToken]);

  const calculateEndTime = (startTime, durationMinutes) => {
    const [hours, minutes] = startTime.split(":").map(Number);
    const startDate = new Date();
    startDate.setHours(hours);
    startDate.setMinutes(minutes + durationMinutes);
    return startDate.toTimeString().slice(0, 5); // Format: HH:MM
  };

  const handleStartTimeChange = (e) => {
    const startTime = e.target.value;
    setSelectedStartTime(startTime);

    if (linkDetails?.service?.duration) {
      const endTime = calculateEndTime(startTime, parseInt(linkDetails.service.duration));
      setSelectedEndTime(endTime);
    }
  };

  const handleBookSession = async () => {
    if (!selectedStartTime || !selectedEndTime || !linkDetails) {
      alert("Please select a date and time.");
      return;
    }

    const payload = {
      start_time: new Date(`${selectedDate}T${selectedStartTime}`).toISOString(),
      end_time: new Date(`${selectedDate}T${selectedEndTime}`).toISOString(),
      therapist: linkDetails.therapist.id,
      service: linkDetails.service.id,
    };

    console.log("Attempting to book session with payload:", payload);

    try {
      await axios.post("https://quirkleai.com/api/sessions/", payload, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      alert("Session booked successfully!");
      navigate("/client-dashboard");
    } catch (err) {
      console.error("Error booking session:", err);
      alert("Failed to book session. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      {linkDetails && (
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader>
            <CardTitle className="text-center text-2xl font-bold text-gray-800">Book a Session</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-b pb-4">
              <h3 className="font-semibold text-lg">{linkDetails.service.name}</h3>
              <p className="text-gray-600">with {linkDetails.therapist.first_name} {linkDetails.therapist.last_name}</p>
            </div>

            <div className="flex justify-between items-center text-lg">
              <span className="font-medium">Price:</span>
              <span className="font-bold">₹{linkDetails.service.price}</span>
            </div>

            <div className="flex justify-between items-center text-lg">
              <span className="font-medium">Duration:</span>
              <span className="font-bold">{linkDetails.service.duration} min</span>
            </div>

            <div className="pt-4 space-y-4">
              <h4 className="font-semibold text-lg">Select Date and Time:</h4>

              <input
                type="date"
                className="w-full border p-2 rounded appearance-none"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />

              <input
                type="time"
                className="w-full border p-2 rounded appearance-none"
                value={selectedStartTime}
                onChange={handleStartTimeChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "ArrowRight") {
                    document.getElementById("confirmButton")?.focus();
                  }
                }}
              />

              <input
                type="time"
                className="w-full border p-2 rounded bg-gray-100 text-gray-700 cursor-not-allowed"
                value={selectedEndTime}
                readOnly
                disabled
              />

              <Button id="confirmButton" onClick={handleBookSession} className="w-full">
                Confirm Booking
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!linkDetails && !loading && error && (
        <div className="text-center text-red-600 font-semibold">{error}</div>
      )}
    </div>
  );
};

export default BookSessionPage;
